import { AbstractSdk, type AbstractSdkConstructorParameters, type ControllerApiConstructor } from '../abstract'
import { Catch } from '../catchError'
import {
  type AuthenticateApplyRequest,
  type AuthenticateUpdateRequest,
  type AuthenticateUpdateResponse,
  AuthenticationControllerV2Api,
  type UserAccountFound,
} from '../generated'
import { type AxiosHeaders } from '../headers'
import { type TrackingSdk } from '../tracking'

type AuthenticationScreenHint = 'SIGN_IN' | 'SIGN_UP'

type AuthenticationUpdateCredentialsRequest = {
  action: string
  request: AuthenticateUpdateRequest
}

type AuthenticationInitRequest = {
  redirectUri: string
  state?: string
  loginHint?: string
  screenHint?: AuthenticationScreenHint
}

type AuthenticationQueryParams = AuthenticationInitRequest &
  Partial<Pick<AuthenticationUpdateCredentialsRequest, 'action'>>

export class AuthenticationV2Sdk extends AbstractSdk<AuthenticationControllerV2Api> {
  readonly #authenticateUrl: string

  constructor(
    readonly trackingSdk: TrackingSdk,
    ...[configuration, ...rest]: AbstractSdkConstructorParameters
  ) {
    super(configuration, ...rest)
    this.#authenticateUrl = `${configuration.basePath}/api/v2/authenticate`
  }

  protected getControllerApiConstructor(): ControllerApiConstructor<AuthenticationControllerV2Api> {
    return AuthenticationControllerV2Api
  }

  private buildQueryParams = ({
    redirectUri,
    state,
    loginHint,
    screenHint,
    action,
  }: AuthenticationQueryParams): URLSearchParams => {
    const urlParams = new URLSearchParams()

    urlParams.set('redirectUri', redirectUri)

    if (loginHint) {
      urlParams.set('loginHint', loginHint)
    }

    if (screenHint) {
      urlParams.set('screenHint', screenHint)
    }

    if (state) {
      urlParams.set('state', state)
    }

    if (action) {
      urlParams.set('action', action)
    }

    urlParams.set('channel', this.userSdk.user.deviceSettings.channel || 'web')

    return urlParams
  }

  /**
   *  init Authentication uri
   *  returns MIDV2 authentication  uri
   */
  initAuthentication = (request: AuthenticationInitRequest): string =>
    `${this.#authenticateUrl}?${this.buildQueryParams(request)}`

  logoutUrl = (request: Pick<AuthenticationInitRequest, 'redirectUri'>): string =>
    `${this.#authenticateUrl}/logout?${this.buildQueryParams(request)}`

  @Catch<UserAccountFound>()
  async authenticate({ code, state, redirectUri }: AuthenticateApplyRequest): Promise<UserAccountFound> {
    try {
      const response = await this.api.authenticateApply(
        this.userSdk.getBffHeader(),
        { code, state, redirectUri },
        this.userSdk.createAxiosOptions(false),
      )
      const { data } = response
      this.userSdk.updateTokenData(data)
      this.trackingSdk.updateEmails(data.emailHidden, data.emailStrong, data.emailStronger)

      return data
    } catch (e) {
      this.userSdk.resetTokenData()
      throw e
    }
  }

  @Catch<UserAccountFound>()
  async reauthenticate(headerOptions?: AxiosHeaders): Promise<UserAccountFound> {
    try {
      const response = await this.api.authenticateRefresh(
        this.userSdk.getBffHeader(),
        {},
        this.userSdk.createAxiosOptions(false, headerOptions),
      )
      const { data } = response

      if (data) {
        this.userSdk.updateTokenData(data)
        this.trackingSdk.updateEmails(data.emailHidden, data.emailStrong, data.emailStronger)
      } else {
        this.userSdk.resetTokenData()
      }

      return data
    } catch (e) {
      this.userSdk.resetTokenData()
      throw e
    }
  }

  async refreshTokensFromInterceptor(): Promise<void> {
    try {
      const response = await this.api.authenticateRefresh(
        this.userSdk.getBffHeader(),
        {},
        this.userSdk.createAxiosOptions(false),
      )
      const { data } = response

      if (data) {
        this.userSdk.notifyRefreshTokenData(data)
        this.trackingSdk.updateEmails(data.emailHidden, data.emailStrong, data.emailStronger)
      } else {
        this.userSdk.notifyRefreshTokenError('Not authenticated (no account data from BFF)')
      }
    } catch (e) {
      this.userSdk.notifyRefreshTokenError(e)
    }
  }

  @Catch<object>()
  async postLogout(): Promise<object> {
    const response = await this.api.webLogoutRedirect(
      this.userSdk.getBffHeader(),
      this.userSdk.createAxiosOptions(false),
    )

    return response.data
  }

  @Catch<AuthenticateUpdateResponse>()
  async authenticateUpdateCredentials({
    action,
    request,
  }: AuthenticationUpdateCredentialsRequest): Promise<AuthenticateUpdateResponse> {
    try {
      const response = await this.api.startUpdateAccount(
        action,
        this.userSdk.getBffHeader(),
        { redirectUri: request.redirectUri },
        this.userSdk.createAxiosOptions(false),
      )
      const { data } = response

      return data
    } catch (e) {
      this.userSdk.resetTokenData()
      throw e
    }
  }
}
